import React, { memo, useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { authSelectors } from 'app-components/flux/auth';
import { preferencesV2Selectors } from 'app-components/flux/preferences-v2';
import { rootUiSelectors } from 'app-components/flux/root-ui';

import { DIALOG_TYPE as TRANSACTION_RULE_DIALOG_TYPE } from 'components/Dialogs/TransactionRules/actions';

import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Slide from '@mui/material/Slide';
import Toolbar from '@mui/material/Toolbar';

import { preferencesSelectors } from 'app-components/flux/preferences';

import { makeStyles } from 'tss-react/mui';

import SimplifiAndQBPLogo from 'components/SimplifiAndQBPLogo';
import { useGlobalHotKey } from 'containers/GlobalHotKeys';
import { useAppSelector } from 'hooks/redux';
import { tracker } from 'app-components/utils/core';
import { HoverProvider, useHoverStore } from './HoverContext';
import { useIsNavOpen, useNavExpansion } from './NavExpansionContext';
import { NavBrand } from './NavBrand';
import { RefreshNavItem } from './NavItems';
import { NavScrollArrow } from './NavScrollArrow';
import { NavToggleButton } from './NavToggleButton';
import { NavPopoverV2 } from './Popover';
import {
  navBarWidth,
  navBarWidthCollapsed,
  navBarWidthOpen,
  useNavBarStyle,
  useNavItemStyles,
} from './commonNavStyles';

const useStyles = makeStyles<void, 'appBarContainerSectioned' | 'appBarContainerCollapsed'>()((theme, _, classes) => ({
  overlayPaper: {
    width: navBarWidthOpen,
    maxWidth: '100vw',
    backgroundImage: 'none',
    '&&': {
      boxShadow: theme.shadows[16],
    },
  },
  staticNav: {
    zIndex: theme.zIndex.drawer,
    backgroundColor: theme.palette.background.paper,
    width: navBarWidth,
    left: 0,
    height: '100%',
  },
  navItemsHolder: {
    display: 'flex',
    flexDirection: 'column-reverse',
    marginTop: 0,
    textAlign: 'left',
    width: '100%',
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 0,
    padding: 0,
    alignItems: 'baseline',
    border: 0,
  },
  rootButton: {
    minWidth: 'unset',
  },
  logoSection: {
    height: theme.defaults?.headers.subHeaderHeightTall,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  logoSectionSectioned: {
    flexShrink: 0,
    height: 'auto',
    paddingTop: theme.spacing(1),
  },
  navButtonBottom: {
    display: 'flex',
    flexDirection: 'column',
    flexFlow: 'column',
    textAlign: 'center',
  },
  navButtonBottomSectioned: {
    boxShadow: `inset 0 1px 0 ${theme.palette.divider}`,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1.5),
  },
  betaLabel: {
    flex: 'auto',
    fontSize: '18px',
    fontWeight: 200,
    letterSpacing: '.05rem',
    position: 'absolute',
    right: 5,
    top: 10,
  },
  appDiv: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minWidth: navBarWidth,
  },
  appDivSectioned: {
    minWidth: navBarWidthCollapsed,
  },
  navScrollArea: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    [`&:has(> [data-direction]) > .${classes.appBarContainerSectioned}`]: {
      paddingBottom: theme.spacing(2),
    },
    [`&:has(> [data-direction]) > .${classes.appBarContainerCollapsed}`]: {
      paddingBottom: theme.spacing(3),
    },
  },
  appBarContainer: {
    overflowY: 'auto',
    maxWidth: '100%',
    overflowX: 'hidden',
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.applyOpacityToHex?.(theme.palette.icon.main, 0.5)} ${theme.palette.background.paper}`,
    background: theme.palette.background.paper,
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.applyOpacityToHex?.(theme.palette.icon.main, 0.5),
      borderRadius: 8,
    },
    '&::-webkit-scrollbar': {
      backgroundColor: theme.palette.background.paper,
      width: 5,
    },
  },
  appBarContainerSectioned: {
    scrollbarWidth: 'none',
    scrollPaddingBlock: theme.spacing(3),
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  appBarContainerCollapsed: {},
  navSpacer: {
    flex: '1 1 0',
    minHeight: 0,
  },
  navSpacerExpandable: {
    cursor: 'pointer',
  },
  navBottomSection: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginBottom: 32,
    overflow: 'hidden',
    flexShrink: 0,
  },
  navBottomSectionSectioned: {
    marginBottom: 0,
  },
}));

const Header = React.memo(function Header() {
  const { classes } = useStyles();
  const { classes: navBarClasses, cx } = useNavBarStyle();
  const { classes: navItemClasses } = useNavItemStyles();

  const authenticated: boolean = useAppSelector((state) => authSelectors.isAuthenticated(state));
  const hideNavButtons = useAppSelector(
    (state) => preferencesV2Selectors.getPreferenceValue(state, 'gsfScreen', 'completed_gsf') !== 'completed_gsf',
  );
  const disableNav = useAppSelector((state) =>
    (rootUiSelectors.getActiveModalsAsJS(state) as Array<{ type: string }>).some(
      ({ type }) => type === TRANSACTION_RULE_DIALOG_TYPE,
    ),
  );
  const hasActiveModal = useAppSelector((state) => rootUiSelectors.getActiveModals(state).size > 0);
  const kioskmode: boolean = useAppSelector(
    (state) =>
      preferencesSelectors.getSharedPreferencesByPath(state, { group: 'dataset', path: ['webApp'] }).kioskmode ?? false,
  );

  const store = useHoverStore();
  const [navScrollEl, setNavScrollEl] = useState<HTMLDivElement | null>(null);
  const isNavOpen = useIsNavOpen();
  const isRailOpen = isNavOpen && !hideNavButtons;
  const { collapsible, canExpand, expanded, toggle, compact, overlayOpen, openOverlay, closeOverlay } =
    useNavExpansion();
  const showOverlay = compact && !hideNavButtons;
  const showToggle = (canExpand || showOverlay) && !hideNavButtons;
  const { pathname } = useLocation();
  const canExpandFromSpacer = showToggle && !expanded;
  const handleSpacerClick = useCallback(() => toggle('nav-spacer'), [toggle]);
  const handleToggleHotKey = useCallback(() => {
    if (!showToggle || hasActiveModal) return;
    if (compact && !overlayOpen) openOverlay('hotkey');
    else toggle('hotkey');
  }, [showToggle, hasActiveModal, compact, overlayOpen, openOverlay, toggle]);
  useGlobalHotKey({
    description: 'Toggle Navigation',
    shiftKey: true,
    altKey: true,
    code: 'KeyN',
    callback: handleToggleHotKey,
  });
  const [bottomRow, setBottomRow] = useState(expanded);
  const [bottomRowOverlay, setBottomRowOverlay] = useState(showOverlay);
  if (bottomRowOverlay !== showOverlay) {
    setBottomRowOverlay(showOverlay);
    setBottomRow(expanded);
  }
  const handleNavTransitionEnd = useCallback(
    (event: React.TransitionEvent) => {
      if (event.target === event.currentTarget && event.propertyName === 'width') {
        setBottomRow(expanded);
      }
    },
    [expanded],
  );

  const closeDrawer = useCallback(() => {
    store.setOverBar(false);
    store.setOverPop(false);
  }, [store]);

  useEffect(() => {
    closeOverlay();
  }, [pathname, closeOverlay]);

  const handleLogoClick = useCallback(() => {
    if (!hideNavButtons) {
      tracker.track(tracker.events.navMenuClick, { menuItem: 'Logo', section: 'main' });
    }
    closeDrawer();
    closeOverlay();
  }, [hideNavButtons, closeDrawer, closeOverlay]);

  const content = (
    <div
      className={cx(
        classes.appDiv,
        collapsible && classes.appDivSectioned,
        collapsible && navItemClasses.sectionedNavItems,
        collapsible && expanded && navItemClasses.sectionedNavItemsExpanded,
      )}
      data-testid='nav-bar-content'
      style={disableNav ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
    >
      <div
        className={cx(classes.logoSection, collapsible && classes.logoSectionSectioned)}
        style={{ cursor: hideNavButtons ? 'default' : undefined }}
      >
        {collapsible ? (
          <NavBrand expanded={isRailOpen} disabled={hideNavButtons} onClick={handleLogoClick} />
        ) : (
          <Button
            onClick={handleLogoClick}
            id='logo-nav'
            classes={{
              root: classes.rootButton,
            }}
            component={NavLink}
            to='/'
            disabled={hideNavButtons}
            style={{ cursor: hideNavButtons ? 'default' : undefined }}
          >
            <SimplifiAndQBPLogo showLogo />
          </Button>
        )}
      </div>
      <div className={classes.navScrollArea}>
        {collapsible && <NavScrollArrow direction='up' scrollParent={navScrollEl} />}
        <div
          ref={setNavScrollEl}
          className={cx(
            classes.appBarContainer,
            collapsible && classes.appBarContainerSectioned,
            collapsible && !expanded && classes.appBarContainerCollapsed,
          )}
          data-testid='nav-scroll-area'
        >
          <Toolbar classes={{ root: classes.toolbar }}>
            <div className={classes.betaLabel}>{`${kioskmode ? '*' : ''}`}</div>

            {!hideNavButtons && (
              <div key='main-nav-items' className={classes.navItemsHolder}>
                <div>
                  <NavPopoverV2 />
                </div>
              </div>
            )}
          </Toolbar>
        </div>
        {collapsible && <NavScrollArrow direction='down' scrollParent={navScrollEl} />}
      </div>
      <div
        className={cx(classes.navSpacer, canExpandFromSpacer && classes.navSpacerExpandable)}
        data-testid='nav-expand-area'
        onClick={canExpandFromSpacer ? handleSpacerClick : undefined}
        aria-hidden
      />
      <div className={cx(classes.navBottomSection, collapsible && classes.navBottomSectionSectioned)}>
        <div
          className={cx(
            classes.navButtonBottom,
            collapsible && classes.navButtonBottomSectioned,
            collapsible && (expanded || bottomRow) && navItemClasses.navBottomRow,
          )}
          data-testid='nav-bottom-section'
        >
          {!collapsible && <div className={navBarClasses.groupDividerStatic} data-testid='nav-bottom-divider' />}
          {!hideNavButtons && <RefreshNavItem key='refresh-all-button' closeDrawer={closeDrawer} />}
          {showToggle && <NavToggleButton />}
        </div>
      </div>
    </div>
  );

  if (showOverlay) {
    return (
      <Drawer
        anchor='left'
        open={overlayOpen}
        onClose={closeOverlay}
        slotProps={{
          paper: {
            id: 'application-header-bar',
            className: cx(navBarClasses.navbar, classes.overlayPaper),
            role: 'dialog',
            'aria-modal': true,
            'aria-label': 'Navigation',
          },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Slide in={authenticated} direction='right'>
      <AppBar
        id='application-header-bar'
        elevation={0}
        position='fixed'
        className={
          hideNavButtons && !collapsible
            ? classes.staticNav
            : cx(
                isRailOpen ? navBarClasses.navbar : navBarClasses.navbarClosed,
                collapsible && navBarClasses.navbarToggle,
                collapsible && !isRailOpen && navBarClasses.navbarToggleClosed,
              )
        }
        onTransitionEnd={collapsible ? handleNavTransitionEnd : undefined}
        onMouseLeave={collapsible ? undefined : () => store.setOverBar(false)}
        onMouseEnter={collapsible ? undefined : () => store.setOverBar(true)}
      >
        {content}
      </AppBar>
    </Slide>
  );
});

const AppHeader = React.memo(function AppHeader() {
  return (
    <HoverProvider>
      <Header />
    </HoverProvider>
  );
});

export default memo(AppHeader);
